import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Button, IconButton, Snackbar, Alert, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useTranslation } from 'react-i18next';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';

interface BirthTimeRectificationWidgetProps {
  chartId: string;
  birthDetails: {
    dateOfBirth: string | Date;
    timeOfBirth: { hour: number; minute: number; second?: number };
  };
  onSaved?: () => void;
  onSaveRectification?: (payload: {
    dateOfBirth: string;
    timeOfBirth: { hour: number; minute: number; second: number };
  }) => Promise<void> | void;
  onAdjust?: (unit: 'month' | 'day' | 'hour' | 'minute' | 'second', delta: number) => Promise<void> | void;
  adjusting?: boolean;
}

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const BirthTimeRectificationWidget: React.FC<BirthTimeRectificationWidgetProps> = ({
  chartId,
  birthDetails,
  onSaved,
  onSaveRectification,
  onAdjust,
  adjusting = false,
}) => {
  const { t } = useTranslation();
  const base = new Date(birthDetails.dateOfBirth);
  const [month,  setMonth]  = useState(base.getUTCMonth() + 1);   // 1-12
  const [day,    setDay]    = useState(base.getUTCDate());
  const [hour,   setHour]   = useState(birthDetails.timeOfBirth.hour);
  const [minute, setMinute] = useState(birthDetails.timeOfBirth.minute);
  const [second, setSecond] = useState(birthDetails.timeOfBirth.second ?? 0);
  const [year]              = useState(base.getUTCFullYear());

  const [saving,   setSaving]   = useState(false);
  const [toast,    setToast]    = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  // Keep in sync if parent passes new birthDetails (e.g. after save)
  useEffect(() => {
    const d = new Date(birthDetails.dateOfBirth);
    setMonth(d.getUTCMonth() + 1);
    setDay(d.getUTCDate());
    setHour(birthDetails.timeOfBirth.hour);
    setMinute(birthDetails.timeOfBirth.minute);
    setSecond(birthDetails.timeOfBirth.second ?? 0);
  }, [birthDetails]);

  const clamp = (v: number, min: number, max: number) =>
    Math.max(min, Math.min(max, v));

  const maxDay = () => new Date(year, month, 0).getDate(); // days in current month

  const inc = (setter: React.Dispatch<React.SetStateAction<number>>, min: number, max: number) =>
    setter(v => v >= max ? min : v + 1);
  const dec = (setter: React.Dispatch<React.SetStateAction<number>>, min: number, max: number) =>
    setter(v => v <= min ? max : v - 1);

  const handleAdjust = async (
    setter: React.Dispatch<React.SetStateAction<number>>,
    min: number,
    max: number,
    delta: -1 | 1,
    unit: 'month' | 'day' | 'hour' | 'minute' | 'second'
  ) => {
    if (delta === 1) {
      inc(setter, min, max);
    } else {
      dec(setter, min, max);
    }

    if (onAdjust) {
      await onAdjust(unit, delta);
    }
  };

  const displayDate = (() => {
    const d = clamp(day, 1, maxDay());
    const h = String(hour).padStart(2, '0');
    const m = String(minute).padStart(2, '0');
    const s = String(second).padStart(2, '0');
    return `${d} ${MONTH_NAMES[month - 1]} ${year} ${h}:${m}:${s}`;
  })();

  const handleSave = async () => {
    setSaving(true);
    try {
      const iso = new Date(Date.UTC(year, month - 1, clamp(day, 1, maxDay()))).toISOString();
      const payload = {
        dateOfBirth: iso,
        timeOfBirth: { hour, minute, second },
      };

      if (onSaveRectification) {
        await onSaveRectification(payload);
      } else {
        await axios.post(`${API_BASE_URL}/charts/${chartId}/rectify`, payload);
      }

      setToast({ msg: t('astrology.birthTimeUpdated'), severity: 'success' });
      onSaved?.();
    } catch (e: any) {
      setToast({ msg: e.response?.data?.message ?? t('astrology.saveFailed'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const Field: React.FC<{
    label: string;
    value: number;
    onInc: () => void;
    onDec: () => void;
    fmt?: (v: number) => string;
  }> = ({ label, value, onInc, onDec, fmt }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 10, lineHeight: 1 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <IconButton
          size="small"
          onClick={onDec}
          disabled={adjusting || saving}
          sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', width: 24, height: 24,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
        >
          <RemoveIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 1, px: 0.75, minWidth: 28, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: 'white', fontWeight: 600, fontSize: 12 }}>
            {fmt ? fmt(value) : String(value).padStart(2, '0')}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onInc}
          disabled={adjusting || saving}
          sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', width: 24, height: 24,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
        >
          <AddIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <>
      <Box sx={{
        border: '1px solid rgba(255,255,255,0.4)',
        borderRadius: 2,
        p: 1.5,
        bgcolor: 'rgba(0,0,0,0.2)',
        minWidth: 360,
      }}>
        <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600, mb: 0.25, textAlign: 'center' }}>
          {t('astrology.birthTimeRectification')}
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', display: 'block', textAlign: 'center', mb: 1 }}>
          {t('astrology.updatedDateTime')}: {displayDate}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Field label={t('astrology.month')} value={month}
            onInc={() => handleAdjust(setMonth, 1, 12, 1, 'month')} onDec={() => handleAdjust(setMonth, 1, 12, -1, 'month')}
            fmt={v => MONTH_NAMES[v - 1]} />
          <Field label={t('astrology.day')} value={clamp(day, 1, maxDay())}
            onInc={() => handleAdjust(setDay, 1, maxDay(), 1, 'day')} onDec={() => handleAdjust(setDay, 1, maxDay(), -1, 'day')} />
          <Field label={t('astrology.hours')} value={hour}
            onInc={() => handleAdjust(setHour, 0, 23, 1, 'hour')} onDec={() => handleAdjust(setHour, 0, 23, -1, 'hour')} />
          <Field label={t('astrology.minutes')} value={minute}
            onInc={() => handleAdjust(setMinute, 0, 59, 1, 'minute')} onDec={() => handleAdjust(setMinute, 0, 59, -1, 'minute')} />
          <Field label={t('astrology.secondsShort')} value={second}
            onInc={() => handleAdjust(setSecond, 0, 59, 1, 'second')} onDec={() => handleAdjust(setSecond, 0, 59, -1, 'second')} />

          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={saving || adjusting}
            sx={{
              mt: 1.5,
              bgcolor: '#2e7d32',
              '&:hover': { bgcolor: '#1b5e20' },
              fontWeight: 600,
              minWidth: 64,
            }}
          >
            {saving ? '…' : t('astrology.save')}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BirthTimeRectificationWidget;
